// The graph's fixed geography, shared by the peek schematic and the
// full-canvas band backdrop so the two always tell the same story. Pure —
// no store or React imports.

import type { ViewMode } from '@/lib/view-mode';

export type Region = 'signals' | 'idea' | 'capabilities' | 'jtbd' | 'assumptions';

export const REGION_ORDER: Region[] = ['signals', 'idea', 'capabilities', 'jtbd', 'assumptions'];

export const REGION_LABELS: Record<Region, string> = {
  signals: 'Market findings',
  idea: 'Idea',
  capabilities: 'Capabilities',
  jtbd: 'Challenges, opportunities & jobs',
  assumptions: 'Assumptions',
};

// Short zone labels for the peek dot map (whisper-faint, tight space).
// REGION_LABELS keeps the long forms the canvas band backdrop uses.
export const REGION_SHORT_LABELS: Record<Region, string> = {
  signals: 'Market findings',
  idea: 'Idea',
  capabilities: 'Capabilities',
  jtbd: 'Customer & jobs',
  assumptions: 'Assumptions',
};

const NODE_TYPE_TO_REGION: Record<string, Region> = {
  market_signal: 'signals',
  idea: 'idea',
  capability: 'capabilities',
  customer: 'jtbd',
  job: 'jtbd',
  challenge: 'jtbd',
  opportunity: 'jtbd',
  assumption: 'assumptions',
};

export function nodeTypeToRegion(type: string): Region | null {
  return NODE_TYPE_TO_REGION[type] ?? null;
}

// Which region a journey step grows. Steps that reason over the whole graph
// (validation, pitch-deck) and the full-graph view itself have no region.
const STEP_TO_REGION: Partial<Record<ViewMode, Region>> = {
  idea: 'idea',
  market: 'signals',
  assumptions: 'assumptions',
  dialog: 'jtbd',
  synthesis: 'jtbd',
  capabilities: 'capabilities',
};

export function stepToRegion(mode: ViewMode): Region | null {
  return STEP_TO_REGION[mode] ?? null;
}

export function deriveRegionCounts(nodes: Array<{ type: string }>): Record<Region, number> {
  const counts = Object.fromEntries(REGION_ORDER.map((r) => [r, 0])) as Record<Region, number>;
  for (const node of nodes) {
    const region = nodeTypeToRegion(node.type);
    if (region) counts[region] += 1;
  }
  return counts;
}

// Graph-coordinate padding around each region's bounding box, and the
// fallback footprint for nodes the caller hasn't measured yet (matches
// BaseNode's typical rendered size closely enough for a backdrop).
export const REGION_PADDING = 40;
const DEFAULT_NODE_WIDTH = 250;
const DEFAULT_NODE_HEIGHT = 80;

export function deriveRegionBoxes(
  nodes: Array<{ type: string; position: { x: number; y: number }; width?: number; height?: number }>,
): Array<{ region: Region; rect: { x: number; y: number; width: number; height: number } }> {
  const bounds = new Map<Region, { minX: number; minY: number; maxX: number; maxY: number }>();
  for (const node of nodes) {
    const region = nodeTypeToRegion(node.type);
    if (!region) continue;
    const w = node.width ?? DEFAULT_NODE_WIDTH;
    const h = node.height ?? DEFAULT_NODE_HEIGHT;
    const b = bounds.get(region) ?? { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
    b.minX = Math.min(b.minX, node.position.x);
    b.minY = Math.min(b.minY, node.position.y);
    b.maxX = Math.max(b.maxX, node.position.x + w);
    b.maxY = Math.max(b.maxY, node.position.y + h);
    bounds.set(region, b);
  }
  return REGION_ORDER.filter((r) => bounds.has(r)).map((region) => {
    const b = bounds.get(region)!;
    return {
      region,
      rect: {
        x: b.minX - REGION_PADDING,
        y: b.minY - REGION_PADDING,
        width: b.maxX - b.minX + 2 * REGION_PADDING,
        height: b.maxY - b.minY + 2 * REGION_PADDING,
      },
    };
  });
}
